/**
 * Theme Store - Gestione stato configuratore UI
 * Utilizza Zustand per state management
 * 
 * IMPORTANTE: Il sistema implementa una separazione tra:
 * - savedTheme: tema persistito (salvato su localStorage)
 * - previewTheme: tema temporaneo di preview (NON persistito)
 * - getTheme(): tema effettivamente usato (savedTheme se livePreview OFF, previewTheme se ON)
 * 
 * Questo permette di modificare il tema in tempo reale senza salvare automaticamente.
 * L'utente deve esplicitamente cliccare "Salva" per persistere le modifiche.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ThemeConfig, ThemePreset, ThemeMode } from '../types/theme';
import { defaultTheme } from '../config/defaultTheme';

interface ThemeState {
  // Tema salvato (persistito) - questo viene mostrato quando livePreview è OFF
  savedTheme: ThemeConfig;
  
  // Tema di preview (temporaneo, NON persistito) - questo viene mostrato quando livePreview è ON
  previewTheme: ThemeConfig;
  
  // Tema originale per reset
  originalTheme: ThemeConfig;
  
  // History for Undo/Redo (usa previewTheme quando in preview mode)
  history: ThemeConfig[];
  historyIndex: number;
  
  // Modalità tema
  mode: ThemeMode;
  
  // Preview
  isPreviewMode: boolean;
  previewDevice: 'desktop' | 'pwa' | 'mobile-web';
  
  // Live preview - quando è ON mostra previewTheme, quando è OFF mostra savedTheme
  // NOTA: livePreview NON affecta il salvataggio automatico!
  // Le modifiche vengono sempre fatte su previewTheme che NON è persistito
  livePreview: boolean;
  
  // Presets salvati
  presets: ThemePreset[];
  currentPresetId: string | null;
  
  // Loading state
  isLoading: boolean;
  isSaving: boolean;
  
  // Getter per il tema corrente
  getTheme: () => ThemeConfig;
  
  // Funzioni - modificano sempre previewTheme (non salvato automaticamente)
  setTheme: (theme: Partial<ThemeConfig>) => void;
  setPreviewTheme: (theme: ThemeConfig) => void;
  resetTheme: () => void;
  setMode: (mode: ThemeMode) => void;
  togglePreviewMode: () => void;
  setPreviewDevice: (device: 'desktop' | 'pwa' | 'mobile-web') => void;
  toggleLivePreview: () => void;
  setLivePreview: (enabled: boolean) => void;
  
  // SALVA - esplicitamente copia previewTheme in savedTheme
  saveTheme: () => void;
  
  // Set savedTheme direttamente (usato da applyToThemeStore)
  setSavedTheme: (theme: ThemeConfig) => void;
  
  // Discard - scarta previewTheme e ripristina da savedTheme
  discardPreview: () => void;
  
  // Undo/Redo
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  
  // Presets
  savePreset: (name: string) => void;
  loadPreset: (presetId: string) => void;
  deletePreset: (presetId: string) => void;
  setCurrentPreset: (presetId: string | null) => void;
  
  // Export/Import
  exportTheme: () => string;
  importTheme: (json: string) => boolean;
  
  // Utility
  getColor: (path: string) => string | undefined;
  setColor: (path: string, value: string) => void;
  
  // Internal helper (not exposed)
  _addToHistory: (newTheme: ThemeConfig) => { history: ThemeConfig[]; historyIndex: number; previewTheme: ThemeConfig };
}

const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
  return path.split('.').reduce((acc: unknown, part) => {
    if (acc && typeof acc === 'object' && part in acc) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
};

const setNestedValue = (obj: Record<string, unknown>, path: string, value: unknown): void => {
  const parts = path.split('.');
  const lastIndex = parts.length - 1;
  
  parts.reduce((acc: Record<string, unknown>, part, index) => {
    if (index === lastIndex) {
      acc[part] = value;
    } else {
      if (!(part in acc) || typeof acc[part] !== 'object' || acc[part] === null) {
        acc[part] = {};
      }
      return acc[part] as Record<string, unknown>;
    }
    return acc;
  }, obj);
};

// Helper to check if object
const isObject = (item: unknown): boolean => {
  return item !== null && typeof item === 'object' && !Array.isArray(item);
};

// Deep merge function
const deepMerge = (target: unknown, source: unknown): unknown => {
  const output: Record<string, unknown> = { ...(target as Record<string, unknown>) };
  if (isObject(target) && isObject(source)) {
    Object.keys(source as object).forEach((key) => {
      const sourceKey = key as string;
      const sourceValue = (source as Record<string, unknown>)[sourceKey];
      if (isObject(sourceValue)) {
        if (!(sourceKey in target)) {
          output[sourceKey] = sourceValue;
        } else {
          output[sourceKey] = deepMerge(
            (target as Record<string, unknown>)[sourceKey],
            sourceValue
          );
        }
      } else {
        output[sourceKey] = sourceValue;
      }
    });
  }
  return output;
};

export const useThemeStore = create<ThemeState>((set, get) => ({
  savedTheme: { ...defaultTheme },
  previewTheme: { ...defaultTheme },
  originalTheme: { ...defaultTheme },
  history: [{ ...defaultTheme }],
  historyIndex: 0,
  mode: 'light',
  isPreviewMode: false,
  previewDevice: 'desktop',
  livePreview: false,
  presets: [],
  currentPresetId: null,
  isLoading: false,
  isSaving: false,

  getTheme: () => {
    const { livePreview, previewTheme, savedTheme } = get();
    return livePreview ? previewTheme : savedTheme;
  },

  _addToHistory: (newTheme: ThemeConfig) => {
    const { history, historyIndex } = get();
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newTheme)));
    if (newHistory.length > 50) {
      newHistory.shift();
    }
    return {
      history: newHistory,
      historyIndex: newHistory.length - 1,
      previewTheme: newTheme,
    };
  },

  setTheme: (partialTheme) => {
    const { previewTheme, _addToHistory } = get();
    const keys = Object.keys(partialTheme);
    let newTheme: ThemeConfig;
    if (keys.length === 1 && keys[0].includes('.')) {
      const path = keys[0];
      const value = partialTheme[path as keyof typeof partialTheme];
      newTheme = JSON.parse(JSON.stringify(previewTheme)) as ThemeConfig;
      setNestedValue(newTheme as unknown as Record<string, unknown>, path, value);
    } else {
      newTheme = deepMerge(previewTheme, partialTheme) as ThemeConfig;
    }
    set({
      ..._addToHistory(newTheme),
      livePreview: true,
    });
  },

  setPreviewTheme: (theme: ThemeConfig) => {
    const { _addToHistory } = get();
    set(_addToHistory(theme));
  },

  resetTheme: () => {
    const { originalTheme, _addToHistory } = get();
    set({
      ..._addToHistory(originalTheme),
      currentPresetId: null,
    });
  },

  undo: () => {
    const { history, historyIndex } = get();
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      set({
        historyIndex: newIndex,
        previewTheme: JSON.parse(JSON.stringify(history[newIndex])),
      });
    }
  },

  redo: () => {
    const { history, historyIndex } = get();
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      set({
        historyIndex: newIndex,
        previewTheme: JSON.parse(JSON.stringify(history[newIndex])),
      });
    }
  },

  canUndo: () => {
    const { historyIndex } = get();
    return historyIndex > 0;
  },

  canRedo: () => {
    const { history, historyIndex } = get();
    return historyIndex < history.length - 1;
  },

  setMode: (mode) => {
    set({ mode });
  },

  togglePreviewMode: () => {
    set((state) => ({ isPreviewMode: !state.isPreviewMode }));
  },

  toggleLivePreview: () => {
    const { livePreview, savedTheme, _addToHistory } = get();
    if (livePreview) {
      set({
        livePreview: false,
        previewTheme: JSON.parse(JSON.stringify(savedTheme)),
        history: [JSON.parse(JSON.stringify(savedTheme))],
        historyIndex: 0,
      });
    } else {
      set({ livePreview: true });
    }
  },

  setLivePreview: (enabled: boolean) => {
    const { livePreview, savedTheme, _addToHistory } = get();
    if (enabled && !livePreview) {
      set({ livePreview: true });
    } else if (!enabled && livePreview) {
      set({
        livePreview: false,
        previewTheme: JSON.parse(JSON.stringify(savedTheme)),
        history: [JSON.parse(JSON.stringify(savedTheme))],
        historyIndex: 0,
      });
    }
  },

  setPreviewDevice: (device) => {
    set({ previewDevice: device });
  },

  saveTheme: () => {
    const { previewTheme } = get();
    set({
      savedTheme: JSON.parse(JSON.stringify(previewTheme)),
    });
  },

  setSavedTheme: (theme) => {
    set({
      savedTheme: JSON.parse(JSON.stringify(theme)),
      previewTheme: JSON.parse(JSON.stringify(theme)),
    });
  },

  discardPreview: () => {
    const { savedTheme, _addToHistory } = get();
    set({
      previewTheme: JSON.parse(JSON.stringify(savedTheme)),
      history: [JSON.parse(JSON.stringify(savedTheme))],
      historyIndex: 0,
      livePreview: false,
    });
  },

  savePreset: (name) => {
    const { previewTheme, presets } = get();
    const preset: ThemePreset = {
      id: `preset-${Date.now()}`,
      name,
      config: JSON.parse(JSON.stringify(previewTheme)),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isDefault: false,
    };
    set({
      presets: [...presets, preset],
      currentPresetId: preset.id,
    });
  },

  loadPreset: (presetId) => {
    const { presets } = get();
    const preset = presets.find((p) => p.id === presetId);
    if (preset) {
      const { _addToHistory } = get();
      set({
        ..._addToHistory(JSON.parse(JSON.stringify(preset.config))),
        currentPresetId: presetId,
      });
    }
  },

  deletePreset: (presetId) => {
    set((state) => ({
      presets: state.presets.filter((p) => p.id !== presetId),
      currentPresetId: state.currentPresetId === presetId ? null : state.currentPresetId,
    }));
  },

  setCurrentPreset: (presetId) => {
    set({ currentPresetId: presetId });
  },

  exportTheme: () => {
    const { previewTheme } = get();
    return JSON.stringify(previewTheme, null, 2);
  },

  importTheme: (json) => {
    try {
      const imported = JSON.parse(json) as ThemeConfig;
      if (!imported.colors || !imported.typography) {
        return false;
      }
      const { _addToHistory } = get();
      set(_addToHistory(imported));
      return true;
    } catch {
      return false;
    }
  },

  getColor: (path) => {
    const { previewTheme } = get();
    const value = getNestedValue(previewTheme as unknown as Record<string, unknown>, path);
    return typeof value === 'string' ? value : undefined;
  },

  setColor: (path, value) => {
    const { previewTheme, _addToHistory } = get();
    const newTheme = JSON.parse(JSON.stringify(previewTheme)) as ThemeConfig;
    setNestedValue(newTheme as unknown as Record<string, unknown>, path, value);
    set({
      ..._addToHistory(newTheme),
      livePreview: true,
    });
  },
}));

export default useThemeStore;
